import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ConfirmModal } from '../components/ConfirmModal';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { APPOINTMENT_STATES } from '../utils/mockData';
import {
  Clock, ChevronLeft, ChevronRight, GripVertical, Pencil, Video,
} from 'lucide-react';
import {
  format, addDays, subDays,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameDay,
  addWeeks, subWeeks, startOfMonth, endOfMonth,
  addMonths, subMonths, eachWeekOfInterval,
} from 'date-fns';
import { es } from 'date-fns/locale';

import { API } from '@/lib/api';
import { timeToMin, minToHHMM, calcEndTime, calcDuration, computeOverlapLayout } from '../utils/calendarHelpers';
import { PX_PER_HOUR, VIEW_TYPES } from '../utils/calendarConstants';
import { NewAppointmentDialog } from '../components/calendar/NewAppointmentDialog';
import { AppointmentDetailSheet } from '../components/calendar/AppointmentDetailSheet';

export const CalendarPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.rol === 'doctor';

  // ── Config de clínica (horarios, slot y horario personalizado) ───────────
  const [clinicConfig, setClinicConfig] = useState({
    work_start:    8,
    work_end:      19,
    slot_duration: 30,
    horario:       null, // HorarioClinica completo si existe
    clinic_phone:  '',
    clinic_name:   '',
  });

  useEffect(() => {
    axios.get(`${API}/config`)
      .then(r => setClinicConfig({
        work_start:    r.data.work_start    ?? 8,
        work_end:      r.data.work_end      ?? 19,
        slot_duration: r.data.slot_duration ?? 30,
        horario:       r.data.horario       ?? null,
        clinic_phone:  r.data.clinic_phone  ?? '',
        clinic_name:   r.data.clinic_name   ?? '',
      }))
      .catch(() => {}); // falla silenciosamente → usa defaults
  }, []);

  // Horas visibles y slots de tiempo calculados desde la config
  const HOURS = useMemo(
    () => Array.from(
      { length: clinicConfig.work_end - clinicConfig.work_start },
      (_, i) => i + clinicConfig.work_start
    ),
    [clinicConfig.work_start, clinicConfig.work_end]
  );

  /**
   * Retorna los slots disponibles para una fecha dada.
   * Respeta la configuración personalizada de la clínica si existe.
   * Retorna Array<{ inicio, fin }> o [] si el día está cerrado.
   */
  const getSlotsForDate = useCallback((fechaStr) => {
    const h = clinicConfig.horario;
    if (!h) {
      // Fallback: generar desde config base cada 10 min para el select
      const slots = [];
      for (let hr = clinicConfig.work_start; hr < clinicConfig.work_end; hr++) {
        for (let m = 0; m < 60; m += 10) {
          const inicio = minToHHMM(hr * 60 + m);
          const fin    = minToHHMM(hr * 60 + m + (clinicConfig.slot_duration || 30));
          slots.push({ inicio, fin });
        }
      }
      return slots;
    }
    const dow = new Date(fechaStr + 'T00:00:00').getDay(); // 0=Dom…6=Sáb
    // Convertir al lunes=0 scheme (0=Lun, 6=Dom)
    const lunesBase = dow === 0 ? 6 : dow - 1;
    if (h.dias_cerrados?.includes(lunesBase)) return [];
    const esSabado = dow === 6;
    const slotsList = esSabado ? h.slots_sabado : h.slots_semana;
    if (slotsList && slotsList.length > 0) return slotsList;
    // Generar automáticamente desde inicio/fin/slot_minutos
    const generated = [];
    for (let min = h.inicio * 60; min < h.fin * 60; min += h.slot_minutos) {
      generated.push({
        inicio: minToHHMM(min),
        fin:    minToHHMM(min + h.slot_minutos),
      });
    }
    return generated;
  }, [clinicConfig]);

  const GRID_HEIGHT = (clinicConfig.work_end - clinicConfig.work_start) * PX_PER_HOUR;
  const pxFromWorkStart = (hhmm) =>
    (timeToMin(hhmm) - clinicConfig.work_start * 60) * (PX_PER_HOUR / 60);

  // ── Datos ─────────────────────────────────────────────────────────────────
  const [currentDate,    setCurrentDate]    = useState(new Date());
  const [view,           setView]           = useState('week');
  const [appointments,   setAppointments]   = useState([]);
  const [patients,       setPatients]       = useState([]);
  const [doctors,        setDoctors]        = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(''); // '' = todos; bloqueado si isDoctor

  // initDone = true una vez que pacientes y doctores ya cargaron (y selectedDoctor fue asignado si es doctor)
  const [initDone, setInitDone] = useState(false);

  /**
   * Retorna los descansos de un doctor para una fecha dada.
   * null = día completo bloqueado (fechas_ausencia). [] = sin descansos.
   */
  const getDoctorBreaks = useCallback((doctorId, fechaStr) => {
    const doc = doctors.find(d => d.id === doctorId);
    if (!doc) return [];
    if (doc.fechas_ausencia?.includes(fechaStr)) return null;
    const dow = new Date(fechaStr + 'T00:00:00').getDay(); // 0=Dom…6=Sáb
    const esSabado = dow === 6;
    return esSabado ? (doc.descansos_sabado || []) : (doc.descansos_semana || []);
  }, [doctors]);

  /**
   * Normaliza los descansos al mismo formato que las citas para
   * poder mezclarlos en computeOverlapLayout.
   */
  const getBreakItemsNorm = useCallback((date) => {
    const fechaStr = format(date, 'yyyy-MM-dd');
    const docIds = selectedDoctor ? [selectedDoctor] : doctors.map(d => d.id);
    const items = [];
    for (const docId of docIds) {
      const breaks = getDoctorBreaks(docId, fechaStr);
      if (!breaks) continue;
      for (const b of breaks) {
        items.push({
          id:          `break-${docId}-${b.inicio}`,
          hora_inicio: b.inicio,
          hora_fin:    b.fin,
          doctor_id:   docId,
          isBreak:     true,
        });
      }
    }
    return items;
  }, [doctors, selectedDoctor, getDoctorBreaks]);

  // Carga inicial: pacientes, doctores y config (una sola vez)
  useEffect(() => {
    const init = async () => {
      try {
        const [patientsRes, doctorsRes] = await Promise.all([
          axios.get(`${API}/patients`, { params: { limit: 500 } }),
          axios.get(`${API}/doctors`),
        ]);
        setPatients(patientsRes.data);
        const activeDoctors = doctorsRes.data.filter(d => d.activo);
        setDoctors(activeDoctors);
        if (isDoctor && user?.email) {
          const myDoc = activeDoctors.find(d => d.email === user.email);
          if (myDoc) setSelectedDoctor(myDoc.id);
        }
      } catch {
        toast.error('Error al cargar datos del calendario');
      } finally {
        setInitDone(true);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: rango de fechas a cargar según la vista activa
  const getFetchRange = useCallback((v, date) => {
    if (v === 'day') {
      const d = format(date, 'yyyy-MM-dd');
      return { fecha_inicio: d, fecha_fin: d };
    }
    if (v === 'week') {
      return {
        fecha_inicio: format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        fecha_fin:    format(endOfWeek(date,   { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    // mes: incluir días del grid completo (semanas parciales)
    return {
      fecha_inicio: format(startOfMonth(date), 'yyyy-MM-dd'),
      fecha_fin:    format(endOfMonth(date),   'yyyy-MM-dd'),
    };
  }, []);

  // Carga reactiva de citas (se re-ejecuta al cambiar vista, fecha o doctor)
  useEffect(() => {
    if (!initDone) return;
    const fetchApts = async () => {
      try {
        const { fecha_inicio, fecha_fin } = getFetchRange(view, currentDate);
        const params = { fecha_inicio, fecha_fin, limit: 500 };
        if (selectedDoctor) params.doctor_id = selectedDoctor;
        const res = await axios.get(`${API}/appointments`, { params });
        setAppointments(res.data);
      } catch {
        toast.error('Error al cargar citas');
      }
    };
    fetchApts();
  }, [initDone, currentDate, view, selectedDoctor, getFetchRange]);

  // ── Scroll al horario actual al montar ────────────────────────────────────
  const gridScrollRef = useRef(null);
  useEffect(() => {
    if (!gridScrollRef.current) return;
    requestAnimationFrame(() => {
      if (!gridScrollRef.current) return;
      const now = new Date();
      const currentMin = now.getHours() * 60 + now.getMinutes();
      const workStartMin = clinicConfig.work_start * 60;
      const px = (currentMin - workStartMin) * (PX_PER_HOUR / 60);
      gridScrollRef.current.scrollTop = Math.max(0, px - 80);
    });
  }, [clinicConfig.work_start, view]);

  // ── Sheet detalles ────────────────────────────────────────────────────────
  const location = useLocation();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [sheetOpen,           setSheetOpen]           = useState(false);
  const [confirmAction,       setConfirmAction]       = useState(null); // { type: 'cancel'|'delete', apt }
  const [isEditMode,          setIsEditMode]          = useState(false);
  const [editData,            setEditData]            = useState(null);
  const [calendarEditOpen,    setCalendarEditOpen]    = useState(false);

  // ── Dialog nueva cita ─────────────────────────────────────────────────────
  const [isCreateOpen,      setIsCreateOpen]      = useState(false);

  // La paleta de comandos (⌘K) y el botón "Nueva cita" del topbar llegan con
  // state.accion: aquí se abre el diálogo y se limpia el state para que un
  // refresh o un "atrás" no lo vuelva a abrir.
  useEffect(() => {
    if (location.state?.accion === 'nueva-cita') {
      setIsCreateOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const [calendarOpen,      setCalendarOpen]       = useState(false);
  const [patientComboOpen,  setPatientComboOpen]  = useState(false);
  const [patientSearch,     setPatientSearch]     = useState('');

  // ── Creación rápida de paciente ───────────────────────────────────────────
  const [quickPatientOpen,   setQuickPatientOpen]   = useState(false);
  const [quickPatientSaving, setQuickPatientSaving] = useState(false);
  const [quickPatientData,   setQuickPatientData]   = useState({ nombre: '', apellido: '', telefono: '' });

  const handleQuickPatientCreate = async () => {
    if (!quickPatientData.nombre.trim() || !quickPatientData.apellido.trim()) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }
    setQuickPatientSaving(true);
    try {
      const res = await axios.post(`${API}/patients`, {
        nombre:          quickPatientData.nombre.trim(),
        apellido:        quickPatientData.apellido.trim(),
        telefono:        quickPatientData.telefono.trim(),
        fecha_nacimiento: '2000-01-01',
        email:           '',
        direccion:       '',
        notas:           '',
      });
      setPatients(prev => [...prev, res.data]);
      setNewApt(prev => ({ ...prev, paciente_id: res.data.id }));
      setQuickPatientOpen(false);
      setQuickPatientData({ nombre: '', apellido: '', telefono: '' });
      toast.success(`Paciente ${res.data.nombre} ${res.data.apellido} creado`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al crear paciente'));
    } finally {
      setQuickPatientSaving(false);
    }
  };
  const [newApt, setNewApt] = useState({
    paciente_id: '',
    doctor_id:   '',
    fecha:       format(new Date(), 'yyyy-MM-dd'),
    hora_inicio: '09:00',
    duracion:    clinicConfig.slot_duration,
    motivo:      '',
    notas:       '',
    modalidad:   'presencial',
  });

  // Sync duracion default con config cuando llegue
  useEffect(() => {
    setNewApt(prev => ({ ...prev, duracion: clinicConfig.slot_duration }));
  }, [clinicConfig.slot_duration]);

  // Si es doctor, pre-cargar su doctor_id en el form de nueva cita
  useEffect(() => {
    if (isDoctor && selectedDoctor) {
      setNewApt(prev => ({ ...prev, doctor_id: selectedDoctor }));
    }
  }, [isDoctor, selectedDoctor]);

  // ── Slots por fecha y detección de ocupados ───────────────────────────────

  // TIME_SLOTS para el modal de nueva cita (depende de la fecha seleccionada)
  const TIME_SLOTS = useMemo(() => {
    const slots = getSlotsForDate(newApt.fecha || format(new Date(), 'yyyy-MM-dd'));
    return slots.map(s => s.inicio);
  }, [getSlotsForDate, newApt.fecha]);

  // EDIT_TIME_SLOTS para el modal de edición (depende de la fecha editada)
  const EDIT_TIME_SLOTS = useMemo(() => {
    const slots = getSlotsForDate(editData?.fecha || format(new Date(), 'yyyy-MM-dd'));
    return slots.map(s => s.inicio);
  }, [getSlotsForDate, editData?.fecha]);

  // Slots ocupados por el doctor en la fecha seleccionada (para nueva cita)
  const occupiedNewApt = useMemo(() => {
    const docId = isDoctor ? selectedDoctor : newApt.doctor_id;
    if (!docId || !newApt.fecha) return new Set();
    return new Set(
      appointments
        .filter(a =>
          a.doctor_id === docId     &&
          a.fecha     === newApt.fecha &&
          a.estado    !== 'cancelada'
        )
        .map(a => a.hora_inicio)
    );
  }, [appointments, isDoctor, selectedDoctor, newApt.doctor_id, newApt.fecha]);

  // Slots ocupados para edición (excluye la cita actual)
  const occupiedEditApt = useMemo(() => {
    if (!editData?.doctor_id || !editData?.fecha || !selectedAppointment) return new Set();
    return new Set(
      appointments
        .filter(a =>
          a.id        !== selectedAppointment.id &&
          a.doctor_id === editData.doctor_id     &&
          a.fecha     === editData.fecha         &&
          a.estado    !== 'cancelada'
        )
        .map(a => a.hora_inicio)
    );
  }, [appointments, editData?.doctor_id, editData?.fecha, selectedAppointment]);

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const [draggedApt, setDraggedApt] = useState(null);

  const handleDragStart = (e, apt) => {
    setDraggedApt(apt);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = async (e, date, slotMin) => {
    e.preventDefault();
    if (!draggedApt) return;
    const newDate      = format(date, 'yyyy-MM-dd');
    const newStart     = minToHHMM(slotMin);
    const originalDur  = calcDuration(draggedApt.hora_inicio, draggedApt.hora_fin);
    const newEnd       = calcEndTime(newStart, originalDur > 0 ? originalDur : clinicConfig.slot_duration);

    setAppointments(prev => prev.map(a =>
      a.id === draggedApt.id ? { ...a, fecha: newDate, hora_inicio: newStart, hora_fin: newEnd } : a
    ));
    try {
      await axios.put(`${API}/appointments/${draggedApt.id}`, {
        paciente_id: draggedApt.paciente_id,
        doctor_id:   draggedApt.doctor_id,
        fecha:       newDate,
        hora_inicio: newStart,
        hora_fin:    newEnd,
        motivo:      draggedApt.motivo,
        notas:       draggedApt.notas || '',
        estado:      draggedApt.estado,
      });
      toast.success(`Cita movida a ${format(date, 'd MMM', { locale: es })} ${newStart}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al mover la cita'));
      setAppointments(prev => prev.map(a => a.id === draggedApt.id ? draggedApt : a));
    }
    setDraggedApt(null);
  };

  // ── Navegación ────────────────────────────────────────────────────────────
  const navigateCalendar = (dir) => {
    if (view === 'day')   setCurrentDate(dir === 'next' ? addDays(currentDate, 1)    : subDays(currentDate, 1));
    else if (view === 'week') setCurrentDate(dir === 'next' ? addWeeks(currentDate, 1)  : subWeeks(currentDate, 1));
    else                  setCurrentDate(dir === 'next' ? addMonths(currentDate, 1)  : subMonths(currentDate, 1));
  };

  const getDateRange = () => {
    if (view === 'day')
      return format(currentDate, "EEEE, d 'de' MMMM yyyy", { locale: es });
    if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end   = endOfWeek(currentDate,   { weekStartsOn: 1 });
      return `${format(start, 'd MMM', { locale: es })} – ${format(end, 'd MMM yyyy', { locale: es })}`;
    }
    return format(currentDate, "MMMM yyyy", { locale: es });
  };

  const weekDaysArray = eachDayOfInterval({
    start: startOfWeek(currentDate, { weekStartsOn: 1 }),
    end:   endOfWeek(currentDate,   { weekStartsOn: 1 }),
  });

  const monthWeeks = (() => {
    const start = startOfMonth(currentDate);
    const end   = endOfMonth(currentDate);
    return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 })
      .map(ws => eachDayOfInterval({ start: ws, end: endOfWeek(ws, { weekStartsOn: 1 }) }));
  })();

  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(a => a.fecha === dateStr);
  };

  /** Pacientes filtrados para el combobox (top 20, busca nombre + teléfono) */
  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) return patients.slice(0, 20);
    const q = patientSearch.toLowerCase();
    return patients
      .filter(p =>
        `${p.nombre} ${p.apellido}`.toLowerCase().includes(q) ||
        (p.telefono || '').includes(q)
      )
      .slice(0, 20);
  }, [patients, patientSearch]);

  /**
   * Detecta si el slot (doctor + fecha + hora_inicio + duracion) solapa
   * con una cita existente del MISMO doctor.
   * Cada doctor tiene su propia agenda — no hay conflicto entre doctores distintos.
   */
  const conflictCheck = useMemo(() => {
    const docId = isDoctor ? selectedDoctor : newApt.doctor_id;
    if (!docId || !newApt.fecha || !newApt.hora_inicio) return null;
    const newStart = timeToMin(newApt.hora_inicio);
    const newEnd   = newStart + newApt.duracion;
    const aptConflict = appointments.find(apt => {
      if (apt.doctor_id !== docId) return false;
      if (apt.fecha     !== newApt.fecha)     return false;
      if (apt.estado    === 'cancelada')      return false;
      const aptStart = timeToMin(apt.hora_inicio);
      const aptEnd   = timeToMin(apt.hora_fin);
      return newStart < aptEnd && newEnd > aptStart;
    }) || null;
    if (aptConflict) return aptConflict;
    const breaks = getDoctorBreaks(docId, newApt.fecha) || [];
    const breakConflict = breaks.find(b => {
      const bStart = timeToMin(b.inicio);
      const bEnd   = timeToMin(b.fin);
      return newStart < bEnd && newEnd > bStart;
    });
    if (breakConflict) return { paciente_nombre: 'Descanso del doctor', hora_inicio: breakConflict.inicio, hora_fin: breakConflict.fin };
    return null;
  }, [isDoctor, selectedDoctor, newApt.doctor_id, newApt.fecha, newApt.hora_inicio, newApt.duracion, appointments, getDoctorBreaks]);

  /** Conflicto para el formulario de edición (excluye la cita que se está editando) */
  const editConflictCheck = useMemo(() => {
    if (!editData || !selectedAppointment) return null;
    if (!editData.doctor_id || !editData.fecha || !editData.hora_inicio) return null;
    const newStart = timeToMin(editData.hora_inicio);
    const newEnd   = newStart + editData.duracion;
    const aptConflict = appointments.find(apt => {
      if (apt.id        === selectedAppointment.id) return false;
      if (apt.doctor_id !== editData.doctor_id)     return false;
      if (apt.fecha     !== editData.fecha)         return false;
      if (apt.estado    === 'cancelada')            return false;
      const aptStart = timeToMin(apt.hora_inicio);
      const aptEnd   = timeToMin(apt.hora_fin);
      return newStart < aptEnd && newEnd > aptStart;
    }) || null;
    if (aptConflict) return aptConflict;
    const breaks = getDoctorBreaks(editData.doctor_id, editData.fecha) || [];
    const breakConflict = breaks.find(b => {
      const bStart = timeToMin(b.inicio);
      const bEnd   = timeToMin(b.fin);
      return newStart < bEnd && newEnd > bStart;
    });
    if (breakConflict) return { paciente_nombre: 'Descanso del doctor', hora_inicio: breakConflict.inicio, hora_fin: breakConflict.fin };
    return null;
  }, [editData, selectedAppointment, appointments, getDoctorBreaks]);

  // ── Handlers de cita ──────────────────────────────────────────────────────
  const handleAppointmentClick = (apt) => {
    setSelectedAppointment(apt);
    setIsEditMode(false);
    setSheetOpen(true);
  };

  const handleAppointmentEditClick = (apt, e) => {
    e.stopPropagation();
    const dur = calcDuration(apt.hora_inicio, apt.hora_fin);
    setSelectedAppointment(apt);
    setEditData({
      doctor_id:   apt.doctor_id,
      fecha:       apt.fecha,
      hora_inicio: apt.hora_inicio,
      duracion:    dur > 0 ? dur : clinicConfig.slot_duration,
      motivo:      apt.motivo,
      notas:       apt.notas || '',
      estado:      apt.estado,
    });
    setIsEditMode(true);
    setSheetOpen(true);
  };

  const handleOpenEdit = () => {
    const dur = calcDuration(selectedAppointment.hora_inicio, selectedAppointment.hora_fin);
    setEditData({
      doctor_id:   selectedAppointment.doctor_id,
      fecha:       selectedAppointment.fecha,
      hora_inicio: selectedAppointment.hora_inicio,
      duracion:    dur > 0 ? dur : clinicConfig.slot_duration,
      motivo:      selectedAppointment.motivo,
      notas:       selectedAppointment.notas || '',
      estado:      selectedAppointment.estado,
      modalidad:   selectedAppointment.modalidad || 'presencial',
    });
    setIsEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.motivo.trim()) { toast.error('El motivo es requerido'); return; }
    const hora_fin = calcEndTime(editData.hora_inicio, editData.duracion);
    try {
      const res = await axios.put(`${API}/appointments/${selectedAppointment.id}`, {
        paciente_id: selectedAppointment.paciente_id,
        doctor_id:   editData.doctor_id,
        fecha:       editData.fecha,
        hora_inicio: editData.hora_inicio,
        hora_fin,
        motivo:      editData.motivo,
        notas:       editData.notas,
        estado:      editData.estado,
        modalidad:   editData.modalidad,
      });
      setAppointments(prev => prev.map(a => a.id === selectedAppointment.id ? res.data : a));
      setSelectedAppointment(res.data);
      setIsEditMode(false);
      toast.success('Cita actualizada');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al actualizar'));
    }
  };

  const handleViewPatient = (apt) => { setSheetOpen(false); navigate(`/patients/${apt.paciente_id}`); };

  const handleCancelAppointment = (apt) => { setSheetOpen(false); setConfirmAction({ type: 'cancel', apt }); };
  const handleDeleteAppointment = (apt) => { setSheetOpen(false); setConfirmAction({ type: 'delete', apt }); };

  const handleUpdateEstado = async (apt, nuevoEstado) => {
    try {
      await axios.put(`${API}/appointments/${apt.id}/status`, null, { params: { estado: nuevoEstado } });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, estado: nuevoEstado } : a));
      setSelectedAppointment(prev => ({ ...prev, estado: nuevoEstado }));
      toast.success(`Estado actualizado: ${nuevoEstado.replace('_', ' ')}`);
    } catch { toast.error('Error al actualizar el estado'); }
  };

  // metodo: 'efectivo' | 'tarjeta' | 'transferencia' al cobrar; null al desmarcar.
  const handleTogglePago = async (apt, metodo = null) => {
    const nuevoPagado = !apt.pagado;
    const nuevoMetodo = nuevoPagado ? metodo : null;
    try {
      await axios.patch(`${API}/appointments/${apt.id}/pago`, {
        pagado: nuevoPagado,
        metodo_pago: nuevoMetodo,
      });
      const cambios = { pagado: nuevoPagado, metodo_pago: nuevoMetodo };
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, ...cambios } : a));
      setSelectedAppointment(prev => ({ ...prev, ...cambios }));
      toast.success(nuevoPagado ? 'Cita marcada como pagada' : 'Cita marcada como no pagada');
    } catch { toast.error('Error al actualizar el estatus de pago'); }
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, apt } = confirmAction;
    setConfirmAction(null);
    try {
      if (type === 'cancel') {
        await axios.put(`${API}/appointments/${apt.id}/status`, null, { params: { estado: 'cancelada' } });
        setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, estado: 'cancelada' } : a));
        setSelectedAppointment(prev => ({ ...prev, estado: 'cancelada' }));
        toast.success('Cita cancelada');
      } else {
        await axios.delete(`${API}/appointments/${apt.id}`);
        setAppointments(prev => prev.filter(a => a.id !== apt.id));
        setSheetOpen(false);
        toast.success('Cita eliminada');
      }
    } catch { toast.error(type === 'cancel' ? 'Error al cancelar la cita' : 'Error al eliminar la cita'); }
  };

  const handleCreateAppointment = async () => {
    const doctorId = isDoctor ? selectedDoctor : newApt.doctor_id;
    if (!newApt.paciente_id || !doctorId || !newApt.motivo) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    const hora_fin = calcEndTime(newApt.hora_inicio, newApt.duracion);
    try {
      const res = await axios.post(`${API}/appointments`, {
        paciente_id: newApt.paciente_id,
        doctor_id:   doctorId,
        fecha:       newApt.fecha,
        hora_inicio: newApt.hora_inicio,
        hora_fin,
        motivo:      newApt.motivo,
        notas:       newApt.notas,
        estado:      'citado',
        modalidad:   newApt.modalidad,
      });
      setAppointments(prev => [...prev, res.data]);
      toast.success(`Cita creada: ${newApt.hora_inicio} – ${hora_fin}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al crear cita'));
      return;
    }
    setIsCreateOpen(false);
    setNewApt({
      paciente_id: '',
      doctor_id:   isDoctor ? selectedDoctor : '',
      fecha:       format(new Date(), 'yyyy-MM-dd'),
      hora_inicio: '09:00',
      duracion:    clinicConfig.slot_duration,
      motivo:      '',
      notas:       '',
      modalidad:   'presencial',
    });
  };

  // ── Renders ───────────────────────────────────────────────────────────────

  /** Tarjeta de cita para la vista Día (más ancha, muestra más info) */
  const renderAptDay = (apt, layoutMap) => {
    const dur    = calcDuration(apt.hora_inicio, apt.hora_fin);
    const top    = pxFromWorkStart(apt.hora_inicio);
    const height = Math.max(dur > 0 ? dur * (PX_PER_HOUR / 60) : PX_PER_HOUR / 2, 22);
    const isShort = height < 40;
    const { col = 0, total = 1 } = layoutMap?.get(apt.id) ?? {};
    const leftPct  = (col  / total) * 100;
    const widthPct = (1    / total) * 100;

    return (
      <div
        key={apt.id}
        draggable
        onDragStart={(e) => handleDragStart(e, apt)}
        onClick={() => handleAppointmentClick(apt)}
        data-testid={`appointment-${apt.id}`}
        className={cn(
          'absolute rounded-md border-l-4 px-2 overflow-hidden group',
          'cursor-grab active:cursor-grabbing hover:brightness-95 transition-all select-none',
          APPOINTMENT_STATES[apt.estado]?.color || 'bg-slate-100',
        )}
        style={{
          top:             top + 1,
          height:          height - 2,
          left:            `calc(${leftPct}% + 2px)`,
          width:           `calc(${widthPct}% - 4px)`,
          borderLeftColor: apt.doctor_color,
          zIndex: 10,
        }}
        title={`${apt.paciente_nombre} · ${apt.motivo} · ${apt.hora_inicio}–${apt.hora_fin}`
               + (apt.hora_por_confirmar ? ' · hora por confirmar con el paciente' : '')}
      >
        {isShort ? (
          <span className="text-[10px] font-semibold leading-tight truncate block">
            {apt.hora_inicio} {apt.paciente_nombre?.split(' ')[0]}
          </span>
        ) : (
          <>
            <div className="flex items-center gap-1 mt-0.5">
              <GripVertical className="h-3 w-3 opacity-40 flex-shrink-0" />
              {apt.modalidad === 'en_linea' && (
                <Video className="h-3 w-3 opacity-70 flex-shrink-0" />
              )}
              <span className="text-xs font-semibold truncate flex-1">{apt.paciente_nombre}</span>
              <button
                onClick={(e) => handleAppointmentEditClick(apt, e)}
                className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 hover:bg-black/10 flex-shrink-0"
                title="Editar cita"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-1 text-[10px] opacity-75">
              <Clock className="h-3 w-3" />
              {apt.hora_inicio} – {apt.hora_fin}
              {/* Seguimiento automático: la hora aún no la confirma el paciente */}
              {apt.hora_por_confirmar
                ? <span className="ml-1 font-semibold text-amber-700 dark:text-amber-400">· por confirmar</span>
                : <span className="ml-1 opacity-60">{dur} min</span>}
            </div>
            {height >= 56 && (
              <div className="text-[10px] opacity-60 truncate">{apt.motivo}</div>
            )}
          </>
        )}
      </div>
    );
  };

  /** Tarjeta de cita para la vista Semana (columnas angostas) */
  const renderAptWeek = (apt, layoutMap) => {
    const dur    = calcDuration(apt.hora_inicio, apt.hora_fin);
    const top    = pxFromWorkStart(apt.hora_inicio);
    const height = Math.max(dur > 0 ? dur * (PX_PER_HOUR / 60) : PX_PER_HOUR / 2, 18);
    const { col = 0, total = 1 } = layoutMap?.get(apt.id) ?? {};
    const leftPct  = (col  / total) * 100;
    const widthPct = (1    / total) * 100;

    return (
      <div
        key={apt.id}
        draggable
        onDragStart={(e) => handleDragStart(e, apt)}
        onClick={() => handleAppointmentClick(apt)}
        data-testid={`appointment-${apt.id}`}
        className="absolute rounded overflow-hidden cursor-grab active:cursor-grabbing hover:brightness-95 transition-all select-none"
        style={{
          top:             top + 1,
          height:          height - 2,
          left:            `calc(${leftPct}% + 1px)`,
          width:           `calc(${widthPct}% - 2px)`,
          backgroundColor: `${apt.doctor_color}22`,
          borderLeft:      `3px solid ${apt.doctor_color}`,
          zIndex:          10,
        }}
        title={`${apt.paciente_nombre} · ${apt.hora_inicio}–${apt.hora_fin} · ${dur} min`}
      >
        <div className="px-1 pt-0.5 text-[10px] font-semibold leading-tight truncate"
             style={{ color: apt.doctor_color }}>
          {apt.paciente_nombre?.split(' ')[0]}
        </div>
        {height >= 34 && (
          <div className="px-1 text-[11px] opacity-70 truncate" style={{ color: apt.doctor_color }}>
            {apt.hora_inicio}
          </div>
        )}
      </div>
    );
  };

  /** Bloque de descanso (fondo estriado) */
  const renderBreakBlock = (item, layoutMap) => {
    const dur    = calcDuration(item.hora_inicio, item.hora_fin);
    const top    = pxFromWorkStart(item.hora_inicio);
    const height = Math.max(dur > 0 ? dur * (PX_PER_HOUR / 60) : PX_PER_HOUR / 2, 16);
    const { col = 0, total = 1 } = layoutMap?.get(item.id) ?? {};
    const leftPct  = (col  / total) * 100;
    const widthPct = (1    / total) * 100;
    const doc      = doctors.find(d => d.id === item.doctor_id);
    const color    = doc?.color || '#0ea5e9';

    return (
      <div
        key={item.id}
        className="absolute pointer-events-none overflow-hidden"
        style={{
          top:        top + 1,
          height:     height - 2,
          left:       `calc(${leftPct}% + 1px)`,
          width:      `calc(${widthPct}% - 2px)`,
          background: `repeating-linear-gradient(45deg, ${color}18, ${color}18 4px, ${color}30 4px, ${color}30 8px)`,
          borderLeft: `2px dashed ${color}70`,
          zIndex:     5,
          opacity:    0.85,
        }}
        title={`Descanso ${item.hora_inicio}–${item.hora_fin}`}
      >
        {height >= 24 && (
          <span className="text-[9px] px-1 leading-tight block truncate opacity-60">
            Descanso
          </span>
        )}
      </div>
    );
  };

  /** Abre el modal de nueva cita con fecha y hora pre-cargadas */
  const handleSlotClick = (date, slotAbsMin) => {
    if (draggedApt) return;
    const fechaStr = format(date, 'yyyy-MM-dd');
    const slots    = getSlotsForDate(fechaStr);
    const matched  = slots.find(s => timeToMin(s.inicio) === slotAbsMin);
    const horaInicio = matched ? matched.inicio : minToHHMM(slotAbsMin);
    const duracion   = matched
      ? timeToMin(matched.fin) - timeToMin(matched.inicio)
      : clinicConfig.slot_duration;
    setNewApt(prev => ({ ...prev, fecha: fechaStr, hora_inicio: horaInicio, duracion }));
    setIsCreateOpen(true);
  };

  /** Grid de líneas de tiempo (fondo del calendario) */
  const TimeGridLines = () => (
    <>
      {HOURS.map((h) => {
        const top = (h - clinicConfig.work_start) * PX_PER_HOUR;
        return (
          <div key={h}>
            {/* Línea de hora — sólida */}
            <div
              className="absolute w-full border-t border-slate-300 dark:border-slate-500"
              style={{ top }}
            />
            {/* Línea de media hora — más sutil */}
            <div
              className="absolute w-full border-t border-slate-200 dark:border-white/10"
              style={{ top: top + PX_PER_HOUR / 2 }}
            />
          </div>
        );
      })}
    </>
  );

  /** Columna de etiquetas horarias — cada 30 min */
  const TimeLabels = () => (
    <div className="relative flex-shrink-0 w-16 select-none" style={{ height: GRID_HEIGHT }}>
      {HOURS.map((h) => (
        <div key={h}>
          {/* Hora en punto */}
          <div
            className="absolute right-2 text-[11px] font-semibold text-slate-500 dark:text-slate-300 leading-none"
            style={{ top: Math.max(2, (h - clinicConfig.work_start) * PX_PER_HOUR - 6) }}
          >
            {h}:00
          </div>
          {/* Media hora */}
          <div
            className="absolute right-2 text-[10px] font-normal text-slate-400 dark:text-slate-500 leading-none"
            style={{ top: (h - clinicConfig.work_start) * PX_PER_HOUR + PX_PER_HOUR / 2 - 5 }}
          >
            {h}:30
          </div>
        </div>
      ))}
    </div>
  );

  /** Celdas para drag & drop y click-to-create — alineadas con los slots reales */
  const DropCells = ({ date }) => {
    const fechaStr = format(date, 'yyyy-MM-dd');
    const daySlots = getSlotsForDate(fechaStr);

    // Si no hay slots configurados, fallback a snap de 30 min
    const cells = daySlots.length > 0
      ? daySlots.map(s => ({
          key:         s.inicio,
          slotAbsMin:  timeToMin(s.inicio),
          top:         (timeToMin(s.inicio) - clinicConfig.work_start * 60) * (PX_PER_HOUR / 60),
          height:      (timeToMin(s.fin) - timeToMin(s.inicio)) * (PX_PER_HOUR / 60),
        }))
      : Array.from({ length: (clinicConfig.work_end - clinicConfig.work_start) * 2 }, (_, i) => {
          const m = i * 30;
          return {
            key:        `m${m}`,
            slotAbsMin: clinicConfig.work_start * 60 + m,
            top:        m * (PX_PER_HOUR / 60),
            height:     30 * (PX_PER_HOUR / 60),
          };
        });

    return (
      <>
        {cells.map(({ key, slotAbsMin, top, height }) => (
          <div
            key={key}
            className="absolute w-full cursor-pointer hover:bg-primary/[0.04] transition-colors"
            style={{ top, height }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, date, slotAbsMin)}
            onClick={() => handleSlotClick(date, slotAbsMin)}
          />
        ))}
      </>
    );
  };

  // ── Indicador de hora actual (línea roja) ────────────────────────────────
  const CurrentTimeLine = () => {
    const now = new Date();
    const nowMin   = now.getHours() * 60 + now.getMinutes();
    const startMin = clinicConfig.work_start * 60;
    const endMin   = clinicConfig.work_end   * 60;
    if (nowMin < startMin || nowMin > endMin) return null;
    const top = (nowMin - startMin) * (PX_PER_HOUR / 60);
    return (
      <div className="absolute w-full flex items-center pointer-events-none" style={{ top, zIndex: 20 }}>
        <div className="w-2.5 h-2.5 rounded-full bg-rose-500 -ml-1.5 flex-shrink-0 shadow shadow-rose-500/50" />
        <div className="flex-1 border-t-2 border-rose-500 opacity-80" />
      </div>
    );
  };

  // ── Vista Día ─────────────────────────────────────────────────────────────
  const renderDayView = () => {
    const dayApts   = getAppointmentsForDate(currentDate);
    const breakNorm = getBreakItemsNorm(currentDate);
    const layoutMap = computeOverlapLayout([...dayApts, ...breakNorm]);
    const isToday   = isSameDay(currentDate, new Date());

    return (
      <div
        ref={gridScrollRef}
        className="overflow-y-auto max-h-[calc(100vh-280px)] mt-2"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div className="flex gap-0">
          <TimeLabels />
          {/* Columna del día */}
          <div
            className={cn(
              'relative flex-1 rounded-lg overflow-hidden',
              isToday && 'bg-primary/[0.02]'
            )}
            style={{ height: GRID_HEIGHT }}
          >
            <TimeGridLines />
            <DropCells date={currentDate} />
            {isToday && <CurrentTimeLine />}
            {breakNorm.map(b => renderBreakBlock(b, layoutMap))}
            {dayApts.map(apt => renderAptDay(apt, layoutMap))}
          </div>
        </div>
      </div>
    );
  };

  // ── Vista Semana ──────────────────────────────────────────────────────────
  const renderWeekView = () => (
    <div className="overflow-x-auto mt-2">
      <div className="min-w-[640px]">
        {/* Header de días — sticky */}
        <div className="flex gap-0 mb-0 pl-16 border-b border-border/40 pb-2">
          {weekDaysArray.map(day => (
            <div
              key={day.toISOString()}
              className={cn(
                'flex-1 text-center py-1 rounded-t-lg',
                isSameDay(day, new Date()) && 'bg-primary/10'
              )}
            >
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {format(day, 'EEE', { locale: es })}
              </div>
              <div className={cn(
                'text-base font-semibold leading-tight',
                isSameDay(day, new Date()) && 'text-primary'
              )}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* Grid scrolleable */}
        <div
          ref={gridScrollRef}
          className="overflow-y-auto max-h-[calc(100vh-280px)]"
          style={{ scrollbarGutter: 'stable' }}
        >
          <div className="flex gap-0">
            <TimeLabels />

            {weekDaysArray.map(day => {
              const dayApts   = getAppointmentsForDate(day);
              const breakNorm = getBreakItemsNorm(day);
              const layoutMap = computeOverlapLayout([...dayApts, ...breakNorm]);
              const isToday   = isSameDay(day, new Date());

              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    'relative flex-1 border-l border-slate-700/50',
                    isToday && 'bg-primary/[0.025]'
                  )}
                  style={{ height: GRID_HEIGHT }}
                >
                  <TimeGridLines />
                  <DropCells date={day} />
                  {isToday && <CurrentTimeLine />}
                  {breakNorm.map(b => renderBreakBlock(b, layoutMap))}
                  {dayApts.map(apt => renderAptWeek(apt, layoutMap))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Vista Mes ─────────────────────────────────────────────────────────────
  const renderMonthView = () => (
    <div className="mt-2 overflow-x-auto">
      <div className="min-w-[480px]">
      <div className="grid grid-cols-7 mb-1">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
          <div key={day} className="text-center py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {monthWeeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1">
            {week.map(day => {
              const dayApts      = getAppointmentsForDate(day);
              const isOtherMonth = day.getMonth() !== currentDate.getMonth();
              const isToday      = isSameDay(day, new Date());

              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    'min-h-[76px] p-1 rounded-lg border',
                    isOtherMonth ? 'opacity-35 border-border/20' : 'border-border/30',
                    isToday && 'bg-primary/5 border-primary/40 ring-1 ring-primary/20'
                  )}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day, 9 * 60)}
                >
                  <div className={cn(
                    'text-sm font-semibold mb-1 leading-none',
                    isToday && 'text-primary'
                  )}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayApts.slice(0, 3).map(apt => (
                      <div
                        key={apt.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, apt)}
                        onClick={() => handleAppointmentClick(apt)}
                        data-testid={`appointment-${apt.id}`}
                        className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:brightness-95 transition-all"
                        style={{
                          backgroundColor: `${apt.doctor_color}20`,
                          color:            apt.doctor_color,
                          borderLeft:      `2px solid ${apt.doctor_color}`,
                        }}
                      >
                        {apt.hora_inicio} {apt.paciente_nombre?.split(' ')[0]}
                      </div>
                    ))}
                    {dayApts.length > 3 && (
                      <div className="text-[10px] text-muted-foreground px-1">
                        +{dayApts.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </div>
    </div>
  );

  // ── Render principal ──────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-page">

        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Calendario</h1>
            <p className="text-muted-foreground mt-1">
              Gestiona las citas · Slot por defecto: <strong>{clinicConfig.slot_duration} min</strong> · Arrastra para mover
            </p>
          </div>

          {/* Dialog nueva cita (componente) */}
          <NewAppointmentDialog
            open={isCreateOpen}
            onOpenChange={setIsCreateOpen}
            newApt={newApt}
            setNewApt={setNewApt}
            patients={patients}
            filteredPatients={filteredPatients}
            doctors={doctors}
            isDoctor={isDoctor}
            selectedDoctor={selectedDoctor}
            timeSlots={TIME_SLOTS}
            occupied={occupiedNewApt}
            conflict={conflictCheck}
            slotDuration={clinicConfig.slot_duration}
            quickPatientOpen={quickPatientOpen}
            setQuickPatientOpen={setQuickPatientOpen}
            quickPatientData={quickPatientData}
            setQuickPatientData={setQuickPatientData}
            quickPatientSaving={quickPatientSaving}
            onQuickPatientCreate={handleQuickPatientCreate}
            patientComboOpen={patientComboOpen}
            setPatientComboOpen={setPatientComboOpen}
            patientSearch={patientSearch}
            setPatientSearch={setPatientSearch}
            calendarOpen={calendarOpen}
            setCalendarOpen={setCalendarOpen}
            onCreate={handleCreateAppointment}
          />
        </div>

        {/* Card del calendario */}
        <Card className="bg-card border border-border/50 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {/* Tabs de vista */}
              <div className="flex bg-muted rounded-lg p-1 w-fit">
                {Object.entries(VIEW_TYPES).map(([key, label]) => (
                  <Button
                    key={key}
                    variant={view === key ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setView(key)}
                    className="text-xs px-3"
                    data-testid={`view-${key}`}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {/* Filtro doctor (oculto para rol doctor) */}
              {!isDoctor && doctors.length > 0 && (
                <Select
                  value={selectedDoctor || '__all__'}
                  onValueChange={v => setSelectedDoctor(v === '__all__' ? '' : v)}
                >
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue placeholder="Todos los doctores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los doctores</SelectItem>
                    {doctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                          {d.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Navegación */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateCalendar('prev')} data-testid="calendar-prev" aria-label="Periodo anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium capitalize min-w-[120px] sm:min-w-[200px] text-center">
                  {getDateRange()}
                </span>
                <Button variant="outline" size="icon" onClick={() => navigateCalendar('next')} data-testid="calendar-next" aria-label="Periodo siguiente">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentDate(new Date())}
                  className="text-xs px-3"
                  data-testid="calendar-today"
                >
                  Hoy
                </Button>
              </div>
            </div>

            {/* Leyenda */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
              {/* Estados */}
              <div className="flex flex-wrap items-center gap-2">
                {Object.entries(APPOINTMENT_STATES).map(([key, { label, color }]) => (
                  <Badge key={key} variant="outline" className={cn('text-xs', color)}>{label}</Badge>
                ))}
              </div>
              {/* Doctores */}
              {doctors.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">Doctores:</span>
                  {doctors.map(doc => (
                    <span key={doc.id} className="flex items-center gap-1 text-[11px]">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: doc.color || '#0ea5e9' }} />
                      {doc.nombre}
                    </span>
                  ))}
                </div>
              )}
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <GripVertical className="h-3 w-3" /> Arrastra para mover
              </span>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            {view === 'day'   && renderDayView()}
            {view === 'week'  && renderWeekView()}
            {view === 'month' && renderMonthView()}
          </CardContent>
        </Card>

        {/* Sheet detalles / edición de cita (componente) */}
        <AppointmentDetailSheet
          open={sheetOpen}
          setOpen={setSheetOpen}
          selectedAppointment={selectedAppointment}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          editData={editData}
          setEditData={setEditData}
          doctors={doctors}
          patients={patients}
          editTimeSlots={EDIT_TIME_SLOTS}
          occupiedEdit={occupiedEditApt}
          editConflict={editConflictCheck}
          calendarEditOpen={calendarEditOpen}
          setCalendarEditOpen={setCalendarEditOpen}
          onOpenEdit={handleOpenEdit}
          onViewPatient={handleViewPatient}
          onCancelApt={handleCancelAppointment}
          onDeleteApt={handleDeleteAppointment}
          onSaveEdit={handleSaveEdit}
          onUpdateEstado={handleUpdateEstado}
          onTogglePago={handleTogglePago}
          clinicPhone={clinicConfig.clinic_phone}
          clinicName={clinicConfig.clinic_name}
        />
      </div>

      <ConfirmModal
        open={!!confirmAction}
        onOpenChange={open => !open && setConfirmAction(null)}
        title={confirmAction?.type === 'cancel' ? '¿Cancelar cita?' : '¿Eliminar cita?'}
        description={confirmAction?.type === 'cancel'
          ? `Se cancelará la cita de ${confirmAction?.apt?.paciente_nombre}. El paciente podrá ser reagendado.`
          : `Se eliminará permanentemente la cita de ${confirmAction?.apt?.paciente_nombre}. Esta acción no se puede deshacer.`}
        variant={confirmAction?.type === 'cancel' ? 'warning' : 'danger'}
        confirmLabel={confirmAction?.type === 'cancel' ? 'Sí, cancelar' : 'Sí, eliminar'}
        cancelLabel="No, mantener"
        onConfirm={executeConfirmAction}
      />
    </Layout>
  );
};
